import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Lock } from "lucide-react";

export default function Articles() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get(`/articles?language=${lang}`).then((r) => setArticles(r.data)).catch(() => {});
  }, [lang]);

  // Free members can read every article marked `tier: free`. Paid
  // articles render with a lock badge and hit the upsell inside
  // `ArticleDetail`. Only unauthenticated visitors still bounce to the
  // Sneak Peek via the top-level `PrivateRoute`.
  const TIER_RANK = { free: 0, basic: 1, premium: 2 };
  const viewerRank = user?.role === "admin"
    ? 99
    : (TIER_RANK[user?.tier || "free"] ?? 0);

  return (
    <div className="bg-[#F8FAFC]">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
        <div className="max-w-3xl mb-16">
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">
            {t("articlesPage.eyebrow")}
          </div>
          <h1 className="font-display font-black text-5xl text-slate-900 leading-tight tracking-tighter">
            {t("articlesPage.title")}
          </h1>
          <p className="mt-4 text-slate-600 text-lg">
            {t("articlesPage.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((a) => {
            const articleRank = TIER_RANK[a.tier] ?? 0;
            const locked = articleRank > viewerRank;
            const Wrapper = locked ? "div" : Link;
            const wrapperProps = locked
              ? { className: "group bg-slate-100 border border-slate-200 rounded-2xl overflow-hidden opacity-70 cursor-not-allowed" }
              : { to: `/articles/${a.id}`, className: "group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300" };
            return (
              <Wrapper
                key={a.id}
                data-testid="articles-card"
                data-locked={locked ? "true" : "false"}
                {...wrapperProps}
              >
                <div className={`relative aspect-[16/10] overflow-hidden ${locked ? "grayscale" : ""}`}>
                  <img
                    src={a.cover_image}
                    alt=""
                    className={`w-full h-full object-cover ${locked ? "" : "group-hover:scale-105 transition-transform duration-500"}`}
                  />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1 bg-white/90 backdrop-blur px-2 py-1 rounded text-[10px] uppercase tracking-widest font-semibold text-slate-900">
                    {a.tier !== "free" && <Lock size={10} />}
                    {t(`tier.${a.tier}`)}
                  </div>
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">
                    {a.category} · {a.read_time}
                  </div>
                  <h3 className={`mt-3 font-display font-bold text-lg leading-snug ${locked ? "text-slate-500" : "text-slate-900 group-hover:text-blue-800"}`}>
                    {a.title}
                  </h3>
                  <p className={`mt-2 text-sm leading-relaxed ${locked ? "text-slate-500" : "text-slate-600"}`}>
                    {a.excerpt}
                  </p>
                  {locked && (
                    <Link
                      to="/pricing"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="articles-upgrade-cta"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-800 underline decoration-amber-400 underline-offset-4"
                    >
                      <Lock size={13} /> {t("articlesPage.upgradeToUnlock")}
                    </Link>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>
      </section>
    </div>
  );
}
